import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { I_S3Credentials } from '../../../interfaces/S3Types';
import {
  Box,
  Card,
  Grid,
  IconButton,
  MenuItem,
  Modal,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography
} from '@mui/material';
import { Device } from '../Log/graph.util';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Button from '@mui/material/Button';
import axiosConfig from '../../../Resources/axiosConfig';
import { UserType } from '../../../interfaces/UserTypes';
import { UserContext } from '../../../contexts/userContext';

interface DetailedBatteryViewProps {
  batteryId: number;
  s3Credentials: I_S3Credentials;
  batteryData: Device;
  installationId: number;
  productNum: number;
}

function DetailedBatteryView(props: DetailedBatteryViewProps) {
  if (props.batteryData === null) {
    return null;
  }
  const intl = useIntl();
  const navigate = useNavigate();
  const [openModalFirmwareUpdate, setOpenModalFirmwareUpdate] = useState(false);
  const [openModalResultFirmwareUpdate, setOpenModalResultFirmwareUpdate] =
    useState(false);
  const [openModalDownloadBatteryLog, setOpenModalDownloadBatteryLog] =
    useState(false);
  const [
    openModalStartDownloadBatteryLog,
    setOpenModalStartDownloadBatteryLog
  ] = useState(false);
  const [openModalError, setOpenModalError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [selectedVersion, setSelectedVersion] = useState(
    props.batteryData.FwVersion
  );

  const handleBatteryViewButton = () => {
    navigate(location.pathname.split('/').slice(0, -2).join('/'));
  };

  const handleUpdateFirmware = () => {
    setOpenModalFirmwareUpdate(true);
  };

  const firmwareModalResultHandleOk = () => {
    navigate(location.pathname.split('/').slice(0, -2).join('/'));
  };

  const [GreenisBlinking, setGreenisBlinking] = useState(
    props.batteryData.Leds.Green === 'Blinking'
  );

  const [AmberisBlinking, setAmberisBlinking] = useState(
    props.batteryData.Leds.Amber === 'Blinking'
  );
  const [RedisBlinking, setRedisBlinking] = useState(
    props.batteryData.Leds.Red === 'Blinking'
  );

  const [BlueisBlinking, setBlueisBlinking] = useState(
    props.batteryData.Leds.Blue === 'Blinking'
  );

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (props.batteryData.Leds.Amber === 'Blinking') {
        setAmberisBlinking((prevIsBlinking) => !prevIsBlinking);
      }

      if (props.batteryData.Leds.Red === 'Blinking') {
        setRedisBlinking((prevIsBlinking) => !prevIsBlinking);
      }

      if (props.batteryData.Leds.Blue === 'Blinking') {
        setBlueisBlinking((prevIsBlinking) => !prevIsBlinking);
      }

      if (props.batteryData.Leds.Green === 'Blinking') {
        setGreenisBlinking((prevIsBlinking) => !prevIsBlinking);
      }
    }, 500); // Blink every 500 milliseconds

    // Cleanup the interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  const batteryStyle = {
    borderRadius: '15px',
    padding: '10px',
    backgroundColor: 'lightgray',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '150px'
  };

  const batteryStringStyle = {
    flex: 1,
    border: '1px solid #000',
    height: '97%',
    width: '30px',
    borderRadius: '7px',
    backgroundColor: '#bfbfbf'
  };

  const context = useContext(UserContext);
  const { currentUser } = context;

  const FirmwareModalHandleProceed = async (e) => {
    setOpenModalFirmwareUpdate(false);

    const res = await axiosConfig
      .post(
        `/UpdateFirmware?batteryNode=${props.batteryId.toString()}&installationId=${
          props.installationId
        }&version=${selectedVersion}`
      )
      .catch((err) => {
        if (err.response) {
          // setError(true);
          // setLoading(false);
        }
      });

    //if (res) {
    setOpenModalResultFirmwareUpdate(true);
    //}
  };

  const FirmwareModalHandleCancel = () => {
    setOpenModalFirmwareUpdate(false);
  };

  const StartDownloadBatteryLogModalHandleOk = () => {
    // stay in the current page which shows the single battery
    setOpenModalStartDownloadBatteryLog(false);
  };

  const handleDownloadBmsLog = () => {
    setOpenModalDownloadBatteryLog(true);
  };

  const DownloadBatteryLogModalHandleCancel = () => {
    setOpenModalDownloadBatteryLog(false);
  };

  const DownloadBatteryLogModalHandleProceed = async () => {
    setOpenModalDownloadBatteryLog(false);
    setOpenModalStartDownloadBatteryLog(true);

    try {
      // Start the job to generate the battery log
      const startRes = await axiosConfig.post(
        `/StartDownloadBatteryLog?batteryNode=${props.batteryId.toString()}&installationId=${
          props.installationId
        }`
      );

      if (startRes.status === 200) {
        const jobId = startRes.data;

        // Polling to check the job status
        const checkJobStatus = async () => {
          try {
            const statusRes = await axiosConfig.get(
              `/GetJobResult?jobId=${jobId}`
            );

            if (statusRes.status === 200) {
              const jobStatus = statusRes.data.status;

              switch (jobStatus) {
                case 'Completed':
                  return statusRes.data.fileName; // Return FileName upon completion
                case 'Failed':
                  throw new Error('Job processing failed.');
                case 'Processing':
                  await new Promise((resolve) => setTimeout(resolve, 60000)); // Wait for 60 seconds before next check
                  return checkJobStatus();
                default:
                  throw new Error('Unknown download battery log job status.');
              }
            } else {
              throw new Error('Unexpected error occurred.');
            }
          } catch (error) {
            throw new Error('Failed to fetch job status.'); // Catch errors from status check
          }
        };

        // Wait for job completion or failure
        const fileName = await checkJobStatus();

        // Once job is completed, download the file
        const res = await axiosConfig.get(
          `/DownloadBatteryLog?jobId=${jobId}`,
          {
            responseType: 'blob'
          }
        );

        const finalFileName = fileName || 'unknown_file_name'; // Default filename if not received
        console.log('Downloaded file name:', finalFileName);

        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = finalFileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);

        // Delete the file after successful download
        console.log('Deleted file name:', finalFileName);
        await axiosConfig.delete(`/DeleteBatteryLog`, {
          params: { fileName: finalFileName }
        });
      } else {
        console.error(
          'Failed to start downloading battery log in the backend.'
        );
      }
    } catch (error) {
      console.error('Error:', error.message);
      setErrorMessage(intl.formatMessage({ id: 'downloadBatteryLogFailed' }));
      setOpenModalError(true);
    } finally {
      setOpenModalStartDownloadBatteryLog(false);
    }
  };

  const ErrorModalHandleOk = () => {
    setOpenModalError(false);
  };

  return (
    <>
      {openModalResultFirmwareUpdate && (
        <Modal
          open={openModalResultFirmwareUpdate}
          aria-labelledby="error-modal"
          aria-describedby="error-modal-description"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 420,
              bgcolor: 'background.paper',
              borderRadius: 4,
              boxShadow: 24,
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <Typography
              variant="body1"
              gutterBottom
              sx={{ fontWeight: 'bold' }}
            >
              <FormattedMessage id="firmwareUpdating" defaultMessage="The firmware is getting updated. Please wait..." />
            </Typography>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginTop: 10
              }}
            >
              <Button
                sx={{
                  marginTop: 2,
                  textTransform: 'none',
                  bgcolor: '#ffc04d',
                  color: '#111111',
                  '&:hover': { bgcolor: '#f7b34d' }
                }}
                onClick={firmwareModalResultHandleOk}
              >
                <FormattedMessage id="ok" defaultMessage="Ok" />
              </Button>
            </div>
          </Box>
        </Modal>
      )}

      {openModalFirmwareUpdate && (
        <Modal
          open={openModalFirmwareUpdate}
          onClose={FirmwareModalHandleCancel}
          aria-labelledby="error-modal"
          aria-describedby="error-modal-description"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 420,
              bgcolor: 'background.paper',
              borderRadius: 4,
              boxShadow: 24,
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <Typography
              variant="body1"
              gutterBottom
              sx={{ fontWeight: 'bold' }}
            >
              <FormattedMessage id="confirmFirmwareUpdate" defaultMessage="Do you really want to update the firmware?" />
            </Typography>

            <Typography variant="body1" gutterBottom>
              <FormattedMessage id="batteryServiceStopWarning" defaultMessage="This action requires the battery service to be stopped for around 10-15 minutes." />
            </Typography>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginTop: 10
              }}
            >
              <Button
                sx={{
                  marginTop: 2,
                  textTransform: 'none',
                  bgcolor: '#ffc04d',
                  color: '#111111',
                  '&:hover': { bgcolor: '#f7b34d' }
                }}
                onClick={FirmwareModalHandleProceed}
              >
                <FormattedMessage id="proceed" defaultMessage="Proceed" />
              </Button>
              <Button
                sx={{
                  marginTop: 2,
                  marginLeft: 2,
                  textTransform: 'none',
                  bgcolor: '#ffc04d',
                  color: '#111111',
                  '&:hover': { bgcolor: '#f7b34d' }
                }}
                onClick={FirmwareModalHandleCancel}
              >
                <FormattedMessage id="cancel" defaultMessage="Cancel" />
              </Button>
            </div>
          </Box>
        </Modal>
      )}

      {openModalStartDownloadBatteryLog && (
        <Modal
          open={openModalStartDownloadBatteryLog}
          aria-labelledby="error-modal"
          aria-describedby="error-modal-description"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 420,
              bgcolor: 'background.paper',
              borderRadius: 4,
              boxShadow: 24,
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <Typography
              variant="body1"
              gutterBottom
              sx={{ fontWeight: 'bold' }}
            >
              <FormattedMessage id="downloadingBatteryLog" defaultMessage="The battery log is getting downloaded. It will be saved in the Downloads folder. Please wait..." />
            </Typography>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginTop: 10
              }}
            >
              <Button
                sx={{
                  marginTop: 2,
                  textTransform: 'none',
                  bgcolor: '#ffc04d',
                  color: '#111111',
                  '&:hover': { bgcolor: '#f7b34d' }
                }}
                onClick={StartDownloadBatteryLogModalHandleOk}
              >
                <FormattedMessage id="ok" defaultMessage="Ok" />
              </Button>
            </div>
          </Box>
        </Modal>
      )}

      {openModalDownloadBatteryLog && (
        <Modal
          open={openModalDownloadBatteryLog}
          onClose={DownloadBatteryLogModalHandleCancel}
          aria-labelledby="error-modal"
          aria-describedby="error-modal-description"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '90vw', sm: 500 },
              bgcolor: 'background.paper',
              borderRadius: 4,
              boxShadow: 24,
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <Typography
              variant="body1"
              gutterBottom
              sx={{ fontWeight: 'bold' }}
            >
              <FormattedMessage id="confirmBatteryLogDownload" defaultMessage="Do you really want to download battery log?" />
            </Typography>

            <Typography variant="body1" gutterBottom>
              <FormattedMessage id="batteryServiceStopWarning" defaultMessage="This action requires the battery service to be stopped for around 10-15 minutes." />
            </Typography>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginTop: 10
              }}
            >
              <Button
                sx={{
                  marginTop: 2,
                  textTransform: 'none',
                  bgcolor: '#ffc04d',
                  color: '#111111',
                  '&:hover': { bgcolor: '#f7b34d' }
                }}
                onClick={DownloadBatteryLogModalHandleProceed}
              >
                <FormattedMessage id="proceed" defaultMessage="Proceed" />
              </Button>
              <Button
                sx={{
                  marginTop: 2,
                  marginLeft: 2,
                  textTransform: 'none',
                  bgcolor: '#ffc04d',
                  color: '#111111',
                  '&:hover': { bgcolor: '#f7b34d' }
                }}
                onClick={DownloadBatteryLogModalHandleCancel}
              >
                <FormattedMessage id="cancel" defaultMessage="Cancel" />
              </Button>
            </div>
          </Box>
        </Modal>
      )}

      {openModalError && (
        <Modal
          open={openModalError}
          aria-labelledby="error-modal"
          aria-describedby="error-modal-description"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 420,
              bgcolor: 'background.paper',
              borderRadius: 4,
              boxShadow: 24,
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <Typography
              variant="body1"
              gutterBottom
              sx={{ fontWeight: 'bold' }}
            >
              {errorMessage}
            </Typography>

            <div
              style={{ display: 'flex', alignItems: 'center', marginTop: 10 }}
            >
              <Button
                sx={{
                  marginTop: 2,
                  textTransform: 'none',
                  bgcolor: '#ffc04d',
                  color: '#111111',
                  '&:hover': { bgcolor: '#f7b34d' }
                }}
                onClick={ErrorModalHandleOk}
              >
                <FormattedMessage id="ok" defaultMessage="Ok" />
              </Button>
            </div>
          </Box>
        </Modal>
      )}

      <Grid container>
        <Grid item xs={6} md={6}>
          <IconButton
            aria-label="go back"
            sx={{
              marginTop:
                currentUser.userType != UserType.admin ? '30px' : '0px',
              backgroundColor: 'grey',
              color: '#000000',
              '&:hover': { bgcolor: '#f7b34d' }
            }}
            onClick={handleBatteryViewButton}
          >
            <ArrowBackIcon />
          </IconButton>

          {currentUser.userType == UserType.admin && (
            <>
              <Select
                value={selectedVersion}
                onChange={(event) => setSelectedVersion(event.target.value)}
                displayEmpty
                variant="outlined"
                sx={{
                  marginTop: '30px',
                  marginLeft: '20px',
                  height: '40px'
                }}
              >
                <MenuItem disabled value="">
                  Select Firmware Version
                </MenuItem>
                <MenuItem value="AF09">AF09</MenuItem>
                <MenuItem value="AF11">AF11</MenuItem>
                <MenuItem value="AF0A">AF0A</MenuItem>
              </Select>
              <Button
                variant="contained"
                onClick={handleUpdateFirmware}
                disabled={!selectedVersion} // Disable button if no version selected
                sx={{
                  marginLeft: '20px',
                  backgroundColor: '#ffc04d',
                  color: '#000000',
                  '&:hover': { bgcolor: '#f7b34d' }
                }}
              >
                Update Firmware
              </Button>
              <Button
                variant="contained"
                onClick={handleDownloadBmsLog}
                sx={{
                  marginLeft: '20px',
                  backgroundColor: '#ffc04d',
                  color: '#000000',
                  '&:hover': { bgcolor: '#f7b34d' }
                }}
              >
                Download Battery Log
              </Button>
            </>
          )}
        </Grid>
      </Grid>
      <Grid container>
        <Grid item md={4.9} xs={4.9}></Grid>
        <Grid item md={2.2} xs={2.2}>
          <Typography
            variant="h6"
            component="div"
            sx={{
              marginLeft: '120px',
              fontWeight: 'bold'
            }}
          >
            {'Node ' + props.batteryId}
          </Typography>

          <div style={batteryStyle}>
            <div
              style={{
                ...batteryStringStyle,
                backgroundColor:
                  props.batteryData.BatteryStrings.String1Active == 'True' ||
                  Number(props.batteryData.BatteryStrings) == 0
                    ? '#32CD32'
                    : '#FF033E'
              }}
            ></div>
            <div
              style={{
                ...batteryStringStyle,
                backgroundColor:
                  props.batteryData.BatteryStrings.String2Active == 'True' ||
                  Number(props.batteryData.BatteryStrings.String2Active) == 0
                    ? '#32CD32'
                    : '#FF033E'
              }}
            ></div>
            <div
              style={{
                ...batteryStringStyle,
                backgroundColor:
                  props.batteryData.BatteryStrings.String3Active == 'True' ||
                  Number(props.batteryData.BatteryStrings.String3Active) == 0
                    ? '#32CD32'
                    : '#FF033E'
              }}
            ></div>
            <div
              style={{
                ...batteryStringStyle,
                backgroundColor:
                  props.batteryData.BatteryStrings.String4Active == 'True' ||
                  Number(props.batteryData.BatteryStrings.String4Active) == 0
                    ? '#32CD32'
                    : '#FF033E'
              }}
            ></div>
            <div
              style={{
                ...batteryStringStyle,
                backgroundColor:
                  props.batteryData.BatteryStrings.String5Active == 'True' ||
                  Number(props.batteryData.BatteryStrings.String5Active) == 0
                    ? '#32CD32'
                    : '#FF033E'
              }}
            ></div>

            <div>
              <div
                style={{
                  width: '20px',
                  height: '20px',
                  marginLeft: '10px',
                  marginTop: '-10px',
                  borderRadius: '50%',
                  backgroundColor:
                    props.batteryData.Leds.Green === 'On' || GreenisBlinking
                      ? 'green'
                      : 'transparent'
                }}
              />

              <div
                style={{
                  width: '20px',
                  height: '20px',
                  marginLeft: '10px',
                  marginTop: '10px',
                  borderRadius: '50%',
                  backgroundColor:
                    props.batteryData.Leds.Amber === 'On' || AmberisBlinking
                      ? 'orange'
                      : 'transparent'
                }}
              />

              <div
                style={{
                  width: '20px',
                  height: '20px',
                  marginLeft: '10px',
                  marginTop: '10px',
                  borderRadius: '50%',
                  backgroundColor:
                    props.batteryData.Leds.Blue === 'On' || BlueisBlinking
                      ? '#00ccff'
                      : 'transparent'
                }}
              />

              <div
                style={{
                  width: '20px',
                  height: '20px',
                  marginLeft: '10px',
                  marginTop: '10px',
                  borderRadius: '50%',
                  backgroundColor:
                    props.batteryData.Leds.Red === 'On' || RedisBlinking
                      ? 'red'
                      : 'transparent'
                }}
              />
            </div>
          </div>
        </Grid>

        <Grid item md={4.9} xs={4.9}></Grid>
      </Grid>

      <Grid item md={3} xs={12}>
        <Card
          sx={{
            overflow: 'visible',
            marginTop: '30px',
            marginBottom: '30px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            border: '2px solid #ccc',
            borderRadius: '12px'
          }}
        >
          <Typography
            variant="h6"
            component="div"
            sx={{
              marginTop: '10px',
              borderBottom: '1px solid #ccc',
              fontWeight: 'bold'
            }}
          >
            Battery Information
          </Typography>

          <TableContainer
            component={Paper}
            sx={{ marginTop: '20px', width: '100%' }}
          >
            <Table size="medium" aria-label="a dense table">
              <TableBody>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    align="left"
                    sx={{ fontWeight: 'bold' }}
                  >
                    Voltage
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '6ch',
                      whiteSpace: 'nowrap',
                      paddingRight: '12px'
                    }}
                  >
                    {props.batteryData.Dc.Voltage + ' V'}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    align="left"
                    sx={{ fontWeight: 'bold' }}
                  >
                    Current
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '6ch',
                      whiteSpace: 'nowrap',
                      paddingRight: '12px'
                    }}
                  >
                    {props.batteryData.Dc.Current + ' A'}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    align="left"
                    sx={{ fontWeight: 'bold' }}
                  >
                    Power
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '6ch',
                      whiteSpace: 'nowrap',
                      paddingRight: '12px'
                    }}
                  >
                    {props.batteryData.Dc.Power + ' W'}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    align="left"
                    sx={{ fontWeight: 'bold' }}
                  >
                    Bus Current
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '6ch',
                      whiteSpace: 'nowrap',
                      paddingRight: '12px'
                    }}
                  >
                    {props.batteryData.BusCurrent + ' A'}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    align="left"
                    sx={{ fontWeight: 'bold' }}
                  >
                    Cells Current
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '6ch',
                      whiteSpace: 'nowrap',
                      paddingRight: '12px'
                    }}
                  >
                    {props.batteryData.CellsCurrent + '  °C'}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    align="left"
                    sx={{ fontWeight: 'bold' }}
                  >
                    Heating Current
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '6ch',
                      whiteSpace: 'nowrap',
                      paddingRight: '12px'
                    }}
                  >
                    {props.batteryData.HeatingCurrent + ' A'}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    align="left"
                    sx={{ fontWeight: 'bold' }}
                  >
                    Soc
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '6ch',
                      whiteSpace: 'nowrap',
                      paddingRight: '12px'
                    }}
                  >
                    {props.batteryData.Soc + ' %'}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Grid>
      {/*----------------------------------------------------------------------------------------------------------------------------------*/}

      {props.productNum === 0 && (
        <>
          <Grid item md={3} xs={12}>
            <Card
              sx={{
                overflow: 'visible',
                marginTop: '30px',
                marginLeft: '20px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                border: '2px solid #ccc',
                borderRadius: '12px'
              }}
            >
              <Typography
                variant="h6"
                component="div"
                sx={{
                  marginTop: '10px',
                  borderBottom: '1px solid #ccc',
                  fontWeight: 'bold'
                }}
              >
                Temperature
              </Typography>

              <TableContainer
                component={Paper}
                sx={{ marginTop: '20px', width: '100%' }}
              >
                <Table size="medium" aria-label="a dense table">
                  <TableBody>
                    <TableRow>
                      <TableCell
                        component="th"
                        scope="row"
                        align="left"
                        sx={{ fontWeight: 'bold' }}
                      >
                        Heating
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          width: '6ch',
                          whiteSpace: 'nowrap',
                          paddingRight: '12px'
                        }}
                      >
                        {props.batteryData.Temperatures.Heating + ' °C'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        component="th"
                        scope="row"
                        align="left"
                        sx={{ fontWeight: 'bold' }}
                      >
                        Board Temperature
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          width: '6ch',
                          whiteSpace: 'nowrap',
                          paddingRight: '12px'
                        }}
                      >
                        {props.batteryData.Temperatures.Board + ' °C'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        component="th"
                        scope="row"
                        align="left"
                        sx={{ fontWeight: 'bold' }}
                      >
                        Center Cells Temperature
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          width: '6ch',
                          whiteSpace: 'nowrap',
                          paddingRight: '12px'
                        }}
                      >
                        {props.batteryData.Temperatures.Cells.Average + ' °C'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        component="th"
                        scope="row"
                        align="left"
                        sx={{ fontWeight: 'bold' }}
                      >
                        Left Cells Temperature
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          width: '6ch',
                          whiteSpace: 'nowrap',
                          paddingRight: '12px'
                        }}
                      >
                        {props.batteryData.Temperatures.Cells.Left + ' °C'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        component="th"
                        scope="row"
                        align="left"
                        sx={{ fontWeight: 'bold' }}
                      >
                        Right Cells Temperature
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          width: '6ch',
                          whiteSpace: 'nowrap',
                          paddingRight: '12px'
                        }}
                      >
                        {props.batteryData.Temperatures.Cells.Right + ' °C'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        component="th"
                        scope="row"
                        align="left"
                        sx={{ fontWeight: 'bold' }}
                      >
                        Average Temperature
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          width: '6ch',
                          whiteSpace: 'nowrap',
                          paddingRight: '12px'
                        }}
                      >
                        {props.batteryData.Temperatures.Cells.Average + '  °C'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        component="th"
                        scope="row"
                        align="left"
                        sx={{ fontWeight: 'bold' }}
                      >
                        State
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          width: '6ch',
                          whiteSpace: 'nowrap',
                          paddingRight: '12px'
                        }}
                      >
                        {props.batteryData.Temperatures.State + '  °C'}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>
        </>
      )}

      {/*----------------------------------------------------------------------------------------------------------------------------------*/}
      <Grid item md={3} xs={12}>
        <Card
          sx={{
            overflow: 'visible',
            marginTop: '30px',
            marginLeft: '20px',
            marginBottom: '20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            border: '2px solid #ccc',
            borderRadius: '12px'
          }}
        >
          <Typography
            variant="h6"
            component="div"
            sx={{
              marginTop: '10px',
              borderBottom: '1px solid #ccc',
              fontWeight: 'bold'
            }}
          >
            Io Status
          </Typography>
          <TableContainer
            component={Paper}
            sx={{ marginTop: '20px', width: '100%' }}
          >
            <Table size="medium" aria-label="a dense table">
              <TableBody>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    align="left"
                    sx={{ fontWeight: 'bold' }}
                  >
                    Connected To Dc Bus
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '6ch',
                      whiteSpace: 'nowrap',
                      paddingRight: '12px'
                    }}
                  >
                    {props.batteryData.IoStatus.ConnectedToDcBus}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    align="left"
                    sx={{ fontWeight: 'bold' }}
                  >
                    Alarm Out Active
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '6ch',
                      whiteSpace: 'nowrap',
                      paddingRight: '12px'
                    }}
                  >
                    {props.batteryData.IoStatus.AlarmOutActive}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    align="left"
                    sx={{ fontWeight: 'bold' }}
                  >
                    Internal Fan Active
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '6ch',
                      whiteSpace: 'nowrap',
                      paddingRight: '12px'
                    }}
                  >
                    {props.batteryData.IoStatus.InternalFanActive}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    align="left"
                    sx={{ fontWeight: 'bold' }}
                  >
                    Volt Measurement Allowed
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '6ch',
                      whiteSpace: 'nowrap',
                      paddingRight: '12px'
                    }}
                  >
                    {props.batteryData.IoStatus.VoltMeasurementAllowed}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    align="left"
                    sx={{ fontWeight: 'bold' }}
                  >
                    Aux Relay Bus
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '6ch',
                      whiteSpace: 'nowrap',
                      paddingRight: '12px'
                    }}
                  >
                    {props.batteryData.IoStatus.AuxRelayBus}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    align="left"
                    sx={{ fontWeight: 'bold' }}
                  >
                    Remote State Active
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '6ch',
                      whiteSpace: 'nowrap',
                      paddingRight: '12px'
                    }}
                  >
                    {props.batteryData.IoStatus.RemoteStateActive}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    align="left"
                    sx={{ fontWeight: 'bold' }}
                  >
                    RiscActive
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '6ch',
                      whiteSpace: 'nowrap',
                      paddingRight: '12px'
                    }}
                  >
                    {props.batteryData.IoStatus.RiscActive}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Grid>

      {/*----------------------------------------------------------------------------------------------------------------------------------*/}

      <Grid item md={3} xs={12}>
        <Card
          sx={{
            overflow: 'visible',
            marginTop: '30px',
            marginLeft: '20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            border: '2px solid #ccc',
            borderRadius: '12px'
          }}
        >
          <Typography
            variant="h6"
            component="div"
            sx={{
              marginTop: '10px',
              borderBottom: '1px solid #ccc',
              fontWeight: 'bold'
            }}
          >
            Specification
          </Typography>
          <TableContainer
            component={Paper}
            sx={{ marginTop: '20px', width: '100%' }}
          >
            <Table size="medium" aria-label="a dense table">
              <TableBody>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    align="left"
                    sx={{ fontWeight: 'bold' }}
                  >
                    Eoc
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '6ch',
                      whiteSpace: 'nowrap',
                      paddingRight: '12px'
                    }}
                  >
                    {props.batteryData.Eoc}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    align="left"
                    sx={{ fontWeight: 'bold' }}
                  >
                    Serial Number
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '6ch',
                      whiteSpace: 'nowrap',
                      paddingRight: '12px'
                    }}
                  >
                    {props.batteryData.SerialNumber}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    align="left"
                    sx={{ fontWeight: 'bold' }}
                  >
                    Firmware Version
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '6ch',
                      whiteSpace: 'nowrap',
                      paddingRight: '12px'
                    }}
                  >
                    {props.batteryData.FwVersion}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    align="left"
                    sx={{ fontWeight: 'bold' }}
                  >
                    Time Since TOC
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '6ch',
                      whiteSpace: 'nowrap',
                      paddingRight: '12px'
                    }}
                  >
                    {props.batteryData.TimeSinceTOC}
                  </TableCell>
                </TableRow>
                {props.productNum === 0 && (
                  <TableRow>
                    <TableCell
                      component="th"
                      scope="row"
                      align="left"
                      sx={{ fontWeight: 'bold' }}
                    >
                      Calibration Charge Requested
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        width: '6ch',
                        whiteSpace: 'nowrap',
                        paddingRight: '12px'
                      }}
                    >
                      {props.batteryData.CalibrationChargeRequested}
                    </TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    align="left"
                    sx={{ fontWeight: 'bold' }}
                  >
                    Max Charge Power
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '6ch',
                      whiteSpace: 'nowrap',
                      paddingRight: '12px'
                    }}
                  >
                    {props.batteryData.MaxChargePower + ' W'}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    align="left"
                    sx={{ fontWeight: 'bold' }}
                  >
                    Max Discharge Power
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '6ch',
                      whiteSpace: 'nowrap',
                      paddingRight: '12px'
                    }}
                  >
                    {props.batteryData.MaxDischargePower + ' W'}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Grid>
    </>
  );
}

export default DetailedBatteryView;
